import { useRouter } from "next/router";

import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Container,
  CssBaseline,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import classNames from "classnames";
import Header from "../header/header";

import { useTranslation } from "next-i18next";
import { useEffect, useMemo, useState } from "react";
import styles from "./fullpage.module.scss";
import { getTheme, Websites } from "@/data/themes";

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const getAltText = (brand: string) => {
  const map: { [key: string]: string } = {
    saseurobonusmc: "SAS Eurobonus world mastercard",
    op: "OP Mastercard",
    circlek: "Circle K Mastercard",
    diners: "Diners Club Mastercard",
    dnb: "DNB Mastercard",
    finnair: "Finnair Mastercard",
    ingo: "Ingo Mastercard",
    jyske: "Jyske Mastercard",
    landkreditt: "Landkreditt Mastercard",
    nknyckeln: "NK Nyckeln Mastercard",
    nordicchoiceclubms: "Strawberry Mastercard",
    strawberry: "Strawberry Mastercard",
    opelkortet: "Opelkortet Mastercard",
    saabkortet: "SAAB-kortet Mastercard",
    sjprio: "SJ Prio Mastercard",
    volvo: "Volvo Mastercard",
    esso: "Esso Mastercard",
    globecard: "GlobeCard Mastercard",
    airplus: "Airplus",
    eurocard: "Eurocard",
    eurocardinternational: "Eurocard International",
    sebkort: "SEB Kort Bank",
    seb: "Skandinaviska Enskilda Banken",
    scandicfriends: "Scandic Friends Mastercard",
  };
  if (map[brand] === undefined) {
    return `${capitalizeFirstLetter(brand)} Mastercard`;
  }
  return map[brand];
};

type Props = {
  useHeader: boolean;
  locale: string;
  brand: string;
  activeStep: number;
  children?: React.ReactNode;
  country: string | string[] | undefined;
  classNameContent?: string;
  website?: Websites;
};
export default function FullPageBranded(props: Readonly<Props>) {
  const {
    useHeader,
    locale,
    brand,
    children,
    activeStep,
    country,
    classNameContent,
    website = Websites.DISPUTE,
  } = props;

  const { t } = useTranslation();
  const router = useRouter();
  const [mode, setMode] = useState<"light" | "dark">("light");

  useEffect(() => {
    if (globalThis.window === undefined) return;

    const { window } = globalThis;
    if (!window.matchMedia) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const applyMode = (isDarkMode: boolean) => {
      setMode(isDarkMode ? "dark" : "light");
    };

    applyMode(mediaQuery.matches);

    const onChange = (event: MediaQueryListEvent) => {
      applyMode(event.matches);
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", onChange);
      return () => {
        mediaQuery.removeEventListener("change", onChange);
      };
    }

    return undefined;
  }, []);

  const theme = useMemo(
    () => getTheme(brand, website, mode),
    [brand, website, mode],
  );

  const getBrandHeader = () => {
    switch (brand) {
      case "seb":
      case "sebselected":
      case "sebcredit":
      case "sebdebit":
      case "sebcorp":
      case "sebcorplimit":
        return (
          <Header>
            <svg
              width="60"
              height="60"
              viewBox="0 0 120 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={classNames([styles.logo, styles.sebLogo])}
            >
              <title>{getAltText(brand)}</title>
              <path d="M120 0H0v120h120V0Z" fill="#60CD18" />
              <path
                d="m43.583 52.252-3.881 2.897c-1.833-1.576-4.2-1.885-5.497-1.885-1.298 0-2.7.377-2.906 1.323-.207.918.646 1.632 2.112 2.03 1.671.45 3.194.848 5.538 1.517 3.486.995 5.87 2.503 5.87 6 0 4.82-4.28 7.319-10.013 7.319s-9.639-2.986-10.8-4.38l4.102-3.067c2.882 2.512 4.886 2.866 6.561 2.866 1.922 0 3.719-.412 3.95-1.5.188-.873-.537-1.567-2.173-1.95-1.853-.427-3.416-.77-5.16-1.23-4.729-1.24-6.062-3.989-6.062-6.458 0-3.4 3.165-6.892 8.954-6.892 5.935 0 9.41 3.4 9.41 3.4l-.005.01Zm24.74-3.069H51.018v21.63h17.617v-4.986h-11.54v-3.483h7.95v-4.77h-7.95v-3.449h11.224l.004-4.942Zm-21.782 26.62h1.35V44.194h-1.35v31.607Zm24.367 0h1.35V44.194h-1.35v31.607Zm4.47-4.99h13.896c3.716 0 6.726-2.24 6.726-5.826 0-2.797-1.832-4.65-4.398-5.47a1.439 1.439 0 0 0-.249-.032c.084-.007.167-.022.249-.043 1.47-.582 3.099-2.131 3.099-4.77 0-3.346-2.961-5.48-6.615-5.48H75.378v21.62Zm6.105-17.124h4.854c1.117 0 2.022.882 2.022 1.895 0 1.014-.9 1.9-2.022 1.9h-4.854v-3.795Zm5.937 12.318h-5.937v-3.943h5.937a1.972 1.972 0 0 1 0 3.941v.002Z"
                fill="#fff"
              />
            </svg>
          </Header>
        );
      case "eurocard":
        return (
          <Header>
            <svg
              viewBox="0 0 143 22"
              width="143"
              height="22"
              className={classNames([styles.logo])}
            >
              <title>{getAltText(brand)}</title>
              <g
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                fillRule="evenodd"
              >
                <path d="M37.945 13.688h-8.777v-1.713h8.13V9.76h-8.13V8.048h8.581v-2.48H25.825v10.688h12.12v-2.568Zm14.543-8.12h-3.342v6.17c0 1.36-.497 1.89-1.866 1.89h-3.314c-1.37 0-1.866-.53-1.866-1.89v-6.17h-3.343v6.88c0 2.656 1.4 3.927 4.472 3.927h4.788c3.071 0 4.471-1.27 4.471-3.927v-6.88Zm14.487 3.854v-.68c0-1.92-.694-3.175-3.268-3.175H53.62v10.689h3.343V13.48h5.254c1.038 0 1.37.37 1.37 1.491v1.284h3.343V14.44c0-1.757-.829-2.185-1.823-2.362v-.03c1.536-.354 1.868-1.195 1.868-2.627Zm-3.252-.075v.355c0 .768-.332 1.122-1.235 1.122h-5.525V8.227h5.225c1.052 0 1.535.176 1.535 1.12ZM77.949 5.45h-5.36c-3.012 0-4.803.738-4.803 3.867v3.19c0 3.129 1.791 3.868 4.803 3.868h5.36c3.012 0 4.803-.74 4.803-3.868v-3.19c0-3.13-1.791-3.867-4.803-3.867Zm1.37 6.038c0 1.55-.33 2.14-1.912 2.14h-4.276c-1.581 0-1.912-.59-1.912-2.14v-1.151c0-1.33.33-2.141 2.093-2.141h3.915c1.761 0 2.093.812 2.093 2.14v1.152Zm14.516 4.887c3.358 0 4.02-1.418 4.02-3.219v-1.003h-3.342c0 1.328-.45 1.476-1.957 1.476h-3.568c-1.582 0-1.912-.592-1.912-2.14v-1.152c0-1.33.33-2.141 2.092-2.141h3.644c.994 0 1.565.222 1.565.99v.294h3.343c.03-3.026-.587-4.03-5.344-4.03h-3.93c-3.012 0-4.804.738-4.804 3.867v3.19c0 3.129 1.792 3.868 4.804 3.868h5.39Zm15.009-2.009 1.04 1.89h3.718L107.61 5.567h-4.352l-5.902 10.689h3.735l.031-.06.947-1.83h6.775Zm-5.525-2.303 2.107-4.015 2.17 4.015h-4.277Zm24.023-2.641v-.68c0-1.92-.693-3.175-3.267-3.175h-10.088v10.689h3.342V13.48h5.254c1.04 0 1.372.37 1.372 1.491v1.284h3.34V14.44c0-1.265-.431-1.836-1.042-2.126a3.073 3.073 0 0 0-.779-.235v-.031c1.535-.354 1.868-1.195 1.868-2.627Zm-3.253-.075v.355c0 .768-.33 1.122-1.234 1.122h-5.526V8.227h5.225c1.054 0 1.535.176 1.535 1.12Zm14.351-3.78h-10.085v10.689h10.085c3.344 0 4.518-1.077 4.518-3.72V9.289c0-2.642-1.174-3.72-4.518-3.72Zm1.175 6.025c0 1.52-.556 1.92-1.987 1.92h-5.932V8.225h5.932c1.431 0 1.987.399 1.987 1.92v1.446ZM5.562 14.108 1.126 9.731l-.008.007a3.732 3.732 0 0 0 0 5.327l5.89 5.813h.002c.006.006.01.013.017.019a3.853 3.853 0 0 0 5.398 0l.007-.008-4.528-4.47 6.889-6.799.005-.005a3.238 3.238 0 0 0 0-4.622l-9.236 9.115Zm7.65.65-.006.004a3.757 3.757 0 0 0 0 5.364l6.42-6.337a3.758 3.758 0 0 0 0-5.363l-6.415 6.332ZM10.831.15a3.628 3.628 0 0 1 0 5.18h.001l-6.31 6.227a3.628 3.628 0 0 1 0-5.18L10.834.15Z" />
              </g>
            </svg>
          </Header>
        );
      case "airplus":
        return (
          <Header>
            <svg
              viewBox="0 0 1664.896924 400"
              height="30"
              fill="currentColor"
              className={classNames([styles.logo])}
            >
              <title>{getAltText(brand)}</title>
              <path d="M99.544212,28.77734L2.448462,394.191548h93.441637l21.402829-93.441637h117.976584l21.402829,93.441637h93.441637L252.496184,28.77734H99.544212ZM133.475561,232.887261l42.805659-185.83921,42.805659,185.83921h-85.611318ZM365.766437.588255h88.743423v73.082846h-88.743423V.588255ZM365.766437,101.860162h88.743423v292.331386h-88.743423V101.860162ZM485.829042,101.860162h88.743448v292.331386h-88.743448V101.860162ZM839.752672,28.77734h-139.900423v365.414208h90.830528v-121.630746h49.069895c91.353558,0,143.033551-42.805659,143.033551-120.586658,0-78.825087-51.679993-123.196805-143.033551-123.196805ZM839.752672,204.698201h-49.069895v-105.970157h49.069895c33.409305,0,50.113934,17.748716,50.113934,53.2461,0,34.453345-16.704628,52.724056-50.113934,52.724056ZM1006.79915,28.77734h88.743436v365.414208h-88.743436V28.77734ZM1308.524845,101.860162h88.743387v292.331386h-88.743387V101.860162ZM1662.448462,300.227916c0,61.598366-48.547899,98.661784-121.10875,98.661784-73.604842,0-120.064565-38.629502-120.064565-98.661784h87.699396c2.088079,24.534947,15.66054,36.019379,39.151497,36.019379,19.836795,0,31.843271-7.830319,31.843271-22.446869,0-52.724056-153.474017-8.874358-153.474017-119.542667,0-60.554326,45.937825-97.617794,115.36651-97.617794,67.862601,0,111.712348,34.975389,111.712348,89.787524h-83.523239c0-17.226721-11.484481-27.14507-31.321276-27.14507-18.792707,0-29.755095,7.308275-29.755095,21.402829,0,49.591938,153.473919,10.440393,153.473919,119.542667ZM668.536134,99.772084h6.264248v90.831563h-14.094555c-37.58545,0-59.510312,2.088079-79.869102,8.352314,9.396366-66.296567,39.151521-99.183877,87.699408-99.183877ZM1266.763225,328.939021c12.528471,0,25.578987-2.610123,35.497384-6.78628-8.352314,48.025855-36.541423,77.259004-85.611318,77.259004-54.812135,0-87.699445-36.019379-87.699445-98.661833V101.860162h88.743436v178.530959c0,30.799183,16.182633,48.547899,49.069943,48.547899Z"></path>
            </svg>
          </Header>
        );
      case "spendwise":
        return (
          <Header>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="120"
              height="20"
              fill="none"
              aria-hidden="true"
              viewBox="0 0 120 20"
            >
              <g clipPath="url(#a)">
                <path
                  fill="#ff7c7a"
                  d="M36.38 4.895c-3.526 0-6.034 2.276-6.034 5.494a4.8 4.8 0 0 0 .386 2.062 5.1 5.1 0 0 0 1.246 1.745c1.087.965 2.697 1.458 4.612 1.458 2.13 0 3.81-.633 5.136-1.947l-1.705-1.598c-1.134 1.05-2.151 1.49-3.5 1.49-1.988 0-3.314-.898-3.43-2.388v-.151h9.271v-.68c0-3.232-2.46-5.485-5.983-5.485Zm3.238 4.416h-6.48v-.198c.188-1.336 1.49-2.19 3.263-2.19 1.774 0 3.053.854 3.217 2.213zM27.614 6.448a5.6 5.6 0 0 0-1.807-1.143 6 6 0 0 0-2.144-.41h-.12c-1.442 0-2.885.413-3.641 1.116l-.142.131h-.095v-.965h-2.722v14.669h2.836v-5.288h.094l.048.043c.686.636 2.271 1.05 3.645 1.05h.116a5.9 5.9 0 0 0 2.143-.395 5.55 5.55 0 0 0 1.808-1.135 5.3 5.3 0 0 0 1.228-1.787 5 5 0 0 0 .382-2.088 4.9 4.9 0 0 0-.4-2.05 5.25 5.25 0 0 0-1.229-1.748m-4.543 6.982c-1.82 0-3.289-1.117-3.289-2.496V9.623c0-1.335 1.537-2.452 3.362-2.452 1.962 0 3.31 1.268 3.31 3.087s-1.395 3.172-3.383 3.172m61.961-1.312h-.12l-3.242-6.94h-2.602l-3.239 6.94h-.097l-2.127-6.94h-2.838l3.122 10.199h2.933L80.3 7.914h.095l3.525 7.463h2.933l3.1-10.2H87.16l-2.127 6.94Zm16.752-2.825c-1.752-.291-3.242-.393-3.242-1.291 0-.68.876-1.093 2.246-1.093 1.61 0 2.864.583 3.362 1.618l2.602-.744c-.759-1.708-3.172-2.888-5.986-2.888-2.958 0-4.921 1.29-4.921 3.206s2.057 2.583 5.535 3.13c1.349.22 2.912.374 2.912 1.316 0 .72-.946 1.116-2.603 1.116-2.035 0-3.15-.583-3.74-1.994l-2.624.583c.305 1.013 1.014 1.881 1.985 2.428 1.112.659 2.555.986 4.26.986 3.336 0 5.466-1.268 5.466-3.24 0-1.643-1.421-2.532-5.252-3.133M92.688.429h-.12c-.398 0-.78.146-1.062.407a1.34 1.34 0 0 0-.44.983c0 .369.158.723.44.983.281.261.664.408 1.062.408h.12c.398 0 .78-.147 1.063-.408.281-.26.44-.614.44-.983a1.34 1.34 0 0 0-.44-.983 1.57 1.57 0 0 0-1.063-.407M54.142 6.206A5.3 5.3 0 0 0 52.393 5.2a5.6 5.6 0 0 0-2.032-.306c-1.443 0-2.98.438-3.95 1.292h-.095V5.177h-2.697V15.38h2.804V9.894c0-1.598 1.37-2.735 3.29-2.735 1.918 0 3.121 1.05 3.121 2.735v5.474h2.814v-5.8c-.016-1.391-.51-2.528-1.506-3.362m65.855 4.854v-.68c0-3.206-2.461-5.471-5.986-5.471S108 7.185 108 10.359c-.024.711.108 1.42.388 2.081a5.2 5.2 0 0 0 1.244 1.77c1.09.965 2.649 1.458 4.543 1.458 2.177 0 3.834-.612 5.205-1.947l-1.702-1.598c-1.137 1.05-2.155 1.49-3.503 1.49-2.01 0-3.311-.921-3.406-2.387v-.167h9.228Zm-9.203-1.904c.167-1.359 1.443-2.213 3.267-2.213 1.774 0 3.05.854 3.217 2.213v.175h-6.484zM66.785 6.031h-.095l-.069-.043c-.85-.7-2.25-1.094-3.692-1.094-3.466 0-5.961 2.277-5.961 5.408a4.95 4.95 0 0 0 .415 2.084 5.3 5.3 0 0 0 1.264 1.767c1.16 1 2.696 1.542 4.282 1.51 1.635 0 3.172-.458 3.976-1.312h.12v1.029h2.554V1.195h-2.794zm0 4.883c0 1.4-1.538 2.495-3.466 2.495-2.107 0-3.525-1.247-3.525-3.107s1.437-3.154 3.516-3.154c1.915 0 3.465 1.096 3.465 2.451zm27.277-5.737h-2.838v10.2h2.838zM12.816 11.15v.093a1 1 0 0 0 0-.093"
                />
                <path
                  fill="url(#b)"
                  d="M6.178 9.115v2.388a7.7 7.7 0 0 1 3.78 1.58c1.434-.257 2.152-.706 2.52-1.12-.232-.285-1.742-2.183-6.3-2.848"
                />
                <path
                  fill="url(#c)"
                  d="M11.36 15.28c.325.956.472 1.955.435 2.956h2.867a10.1 10.1 0 0 0-.898-4.005 8 8 0 0 1-2.404 1.05Z"
                />
                <path
                  fill="#ff7c7a"
                  d="M13.764 14.228c1.043-.764 1.89-1.749 1.89-3.174 0-3.207-4.032-4.332-6.386-4.86-2.816-.632-5.51-.894-5.51-2.165 0-1.006 1.749-1.49 3.69-1.49 3.229 0 4.905 1.315 5.582 1.95l1.991-1.796C12.693.592 9.706 0 7.401 0 3.975 0 .893 1.315.893 4.081c.003 3.405 4.59 3.772 7.77 4.545 3.011.743 4.152 1.816 4.152 2.518a1 1 0 0 1 0 .088.7.7 0 0 1-.038.157c-.567 1.799-4.41 1.871-5.249 1.871-3.711 0-4.48-.81-4.628-1.224-.214-.603.945-.659 1.692-.659.535.001 1.07.042 1.597.123l.152-2.367a13 13 0 0 0-1.847-.163c-2.136 0-4.467.918-4.49 2.8.073 2.584 3.98 3.986 7.72 3.986 1.232.003 2.46-.16 3.642-.481a8 8 0 0 0 2.403-1.05z"
                />
              </g>
              <defs>
                <linearGradient
                  id="b"
                  x1="6.846"
                  x2="12.525"
                  y1="10.103"
                  y2="13.919"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset=".15" stopColor="#ff7876" />
                  <stop offset=".23" stopColor="#ff7876" stopOpacity=".98" />
                  <stop offset=".33" stopColor="#ff7876" stopOpacity=".93" />
                  <stop offset=".43" stopColor="#ff7876" stopOpacity=".84" />
                  <stop offset=".54" stopColor="#ff7876" stopOpacity=".72" />
                  <stop offset=".65" stopColor="#ff7876" stopOpacity=".56" />
                  <stop offset=".76" stopColor="#ff7876" stopOpacity=".36" />
                  <stop offset=".87" stopColor="#ff7876" stopOpacity=".13" />
                  <stop offset=".93" stopColor="#ff7876" stopOpacity="0" />
                </linearGradient>
                <linearGradient
                  id="c"
                  x1="12.898"
                  x2="13.089"
                  y1="13.52"
                  y2="19.867"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#ff7876" />
                  <stop offset=".02" stopColor="#ff7876" stopOpacity=".97" />
                  <stop offset=".29" stopColor="#ff7876" stopOpacity=".56" />
                  <stop offset=".5" stopColor="#ff7876" stopOpacity=".26" />
                  <stop offset=".65" stopColor="#ff7876" stopOpacity=".07" />
                  <stop offset=".73" stopColor="#ff7876" stopOpacity="0" />
                </linearGradient>
                <clipPath id="a">
                  <path fill="#fff" d="M0 0h120v19.843H0z" />
                </clipPath>
              </defs>
            </svg>
          </Header>
        );
      case "saseurobonusmc":
        return (
          <Header>
            <div className={styles.saseurobonusmc}>
              <div className={styles.sasInnerEurobonusmc}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xmlnsXlink="http://www.w3.org/1999/xlink"
                  version="1.1"
                  className={classNames([styles.mastercardLogo])}
                  x="0px"
                  y="0px"
                  viewBox="17 17 118 74"
                  xmlSpace="preserve"
                >
                  <style type="text/css">
                    {`
                    .st0{fill:none;}
                    .st1{fill:#FF5F00;}
                    .st2{fill:#EB001B;}
                    .st3{fill:#F79E1B;}
                    `}
                  </style>
                  <g>
                    <rect y="0" className="st0" width="152.4" height="108" />
                    <g>
                      <rect
                        x="60.4"
                        y="25.7"
                        className="st1"
                        width="31.5"
                        height="56.6"
                      />
                      <path
                        className="st2"
                        d="M62.4,54c0-11,5.1-21.5,13.7-28.3c-15.6-12.3-38.3-9.6-50.6,6.1C13.3,47.4,16,70,31.7,82.3    c13.1,10.3,31.4,10.3,44.5,0C67.5,75.5,62.4,65,62.4,54z"
                      />
                      <path
                        className="st3"
                        d="M134.4,54c0,19.9-16.1,36-36,36c-8.1,0-15.9-2.7-22.2-7.7c15.6-12.3,18.3-34.9,6-50.6c-1.8-2.2-3.8-4.3-6-6    c15.6-12.3,38.3-9.6,50.5,6.1C131.7,38.1,134.4,45.9,134.4,54z"
                      />
                    </g>
                  </g>
                </svg>
                <Typography
                  className={styles.mastercard}
                  variant="body2"
                  aria-hidden="true"
                >
                  Mastercard
                </Typography>
              </div>
              <Typography
                variant="body2"
                className={styles.sasHeaderCompanyName}
              >
                SAS Eurobonus World Mastercard
              </Typography>
            </div>
          </Header>
        );

      // all JPEG's
      case "ingo":
      case "sjprio":
      case "scandicfriends":
      case "strawberry":
      case "circlek":
      case "dnb":
        return (
          <Header className={styles[brand]}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              height="37"
              src={`/brands/${brand}.jpg`}
              alt={getAltText(brand)}
            />
          </Header>
        );

      // all PNG's
      case "jyske":
      case "esso":
      case "op":
        return (
          <Header className={styles[brand]}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              height="37"
              src={`/brands/${brand}.png`}
              alt={getAltText(brand)}
            />
          </Header>
        );

      default:
        return (
          <Header className={styles.header}>
            <div />
          </Header>
        );
    }
  };

  const nessesaryCookies = [
    {
      name: "PGNET",
      description: t(
        "Used to anonymously identify requests from the same browser when browsing the website.",
      ),
      provider: "SEB kort",
      expiration: t("End of visit"),
    },
    {
      name: "BIGipServer~*",
      description: t(
        "Used by the load balancer to provide availability and scalability to the application and ensure that a visitor has a consistent experience.",
      ),
      provider: "SEB kort",
      expiration: t("End of visit"),
    },
    {
      name: "C0WNET",
      description: t(
        "Used to anonymously identify requests from the same browser when browsing the website.",
      ),
      provider: "SEB kort",
      expiration: t("End of visit"),
    },
    {
      name: "csrf_token",
      description: t(
        "Used to anonymously identify requests from the same browser when browsing the website.",
      ),
      provider: "SEB kort",
      expiration: t("End of visit"),
    },
  ];

  const [showDialog, setShowDialog] = useState(false);

  const markets = [
    {
      label: t("International"),
      value: "en",
    },
    {
      label: t("Sweden"),
      value: "sv",
    },
    {
      label: t("Norway"),
      value: "no",
    },
    {
      label: t("Denmark"),
      value: "da",
    },
    {
      label: t("Finland"),
      value: "fi",
    },
    {
      label: t("Germany"),
      value: "de",
    },
    {
      label: t("France"),
      value: "fr",
    },
  ];
  const getCurrentMarket = (): { label: string; value: string } => {
    const wanted = typeof country === "string" ? country : locale;
    const result = markets.filter((market) => market.value === wanted);
    if (result.length) {
      return result[0];
    }
    // German/French are languages only, they have no market of their own
    return markets[0];
  };
  const languages = [
    {
      label: "English",
      value: "en",
    },
    {
      label: "Svenska",
      value: "sv",
    },
    {
      label: "Norsk",
      value: "no",
    },
    {
      label: "dansk",
      value: "da",
    },
    {
      label: "Suomeksi",
      value: "fi",
    },
    {
      label: "Deutsch",
      value: "de",
    },
    {
      label: "Français",
      value: "fr",
    },
  ];
  const getCurrentLanguage = (): { label: string; value: string } => {
    const result = languages.filter((language) => {
      return language.value === locale;
    });
    if (result.length) {
      return result[0];
    }
    return {
      label: "",
      value: "",
    };
  };

  const onChangeLanguageSelect = (e: SelectChangeEvent<string>) => {
    const newLocale = e.target.value;
    const [pathname, search] = router.asPath.split("?");
    const params = new URLSearchParams(search || "");

    if (!params.has("country")) {
      params.set("country", getCurrentMarket().value);
    }

    const currentCountry = params.get("country");
    if (currentCountry === newLocale) {
      params.delete("country");
    }

    const queryString = params.toString();
    const newPath = queryString ? `${pathname}?${queryString}` : pathname;

    router.push(newPath, newPath, {
      locale: newLocale,
      scroll: false,
    });
  };

  const onChangeMarketSelect = (e: SelectChangeEvent<string>) => {
    const newCountry = e.target.value.replaceAll(/[^a-zA-Z-]/g, "");
    const [pathname, search] = router.asPath.split("?");
    const params = new URLSearchParams(search || "");

    if (locale === newCountry) {
      params.delete("country");
    } else {
      params.set("country", newCountry);
    }

    const queryString = params.toString();
    const newPath = queryString ? `${pathname}?${queryString}` : pathname;

    router.push(newPath, newPath, { locale: locale, scroll: false });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {useHeader ? (
        <div className={useHeader ? styles.useHeader : ""} aria-live="polite">
          {getBrandHeader()}

          <Container className={classNames(styles.page, classNameContent)}>
            {children}
          </Container>

          <footer className={styles.footer}>
            <Container className={classNames(styles.page)}>
              {activeStep > 0 ? (
                <div />
              ) : (
                <Stack direction="row" sx={{ gap: 1 }}>
                  <FormControl size="small">
                    <InputLabel id="demo-simple-select-label">
                      {t("Market")}
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={getCurrentMarket().value}
                      label={t("Market")}
                      onChange={onChangeMarketSelect}
                    >
                      {markets.map((market) => (
                        <MenuItem value={market.value} key={market.value}>
                          {market.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small">
                    <InputLabel id="demo-simple-select-label">
                      {t("Language")}
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={getCurrentLanguage().value}
                      label={t("Language")}
                      onChange={onChangeLanguageSelect}
                    >
                      {languages.map((language) => (
                        <MenuItem value={language.value} key={language.value}>
                          {language.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              )}

              <Typography mt={3}>
                {t("This page uses")}{" "}
                <Link
                  component="button"
                  aria-controls="scard-cookies-dialog"
                  aria-expanded={showDialog}
                  onClick={() => setShowDialog(!showDialog)}
                >
                  {t("nessesary cookies")}
                </Link>
                .
              </Typography>
              <Dialog
                open={showDialog}
                id="scard-cookies-dialog"
                onClose={() => setShowDialog(!showDialog)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
              >
                <DialogTitle id="alert-dialog-title">
                  {t("Nessesary cookies")}
                  <IconButton
                    aria-label={
                      t("Close cookie dialog") || "Close cookie dialog"
                    }
                    onClick={() => setShowDialog(!showDialog)}
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: 8,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <div id="alert-dialog-description">
                    {nessesaryCookies.map((cookie) => (
                      <Accordion
                        key={cookie.name}
                        disableGutters
                        elevation={0}
                        className={styles.accordion}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography component="h2" variant="h6">
                            {cookie.name}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography mb={1}>{cookie.description}</Typography>
                          <Typography component="h3" variant="body1">
                            {t("Provider")}:
                          </Typography>
                          <Typography mb={1}>{cookie.provider}</Typography>
                          <Typography component="h3" variant="body1">
                            {t("Expiration")}:
                          </Typography>
                          <Typography>{cookie.expiration}</Typography>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>

              <Typography mt={3}>&copy; SEB Kort Bank AB</Typography>
            </Container>
          </footer>
        </div>
      ) : (
        <div className={classNames(styles.page)} aria-live="polite">
          {children}
        </div>
      )}
    </ThemeProvider>
  );
}
